// TAPINTO® Editor Frontend - Properties Panel
// Version: 1.0
// Date: 2025-10-01

import React from 'react';
import { useEditor } from '@craftjs/core';
import { Settings, Palette, Type, Layout } from 'lucide-react';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

export function PropertiesPanel() {
  const { selected, actions, query } = useEditor((state, query) => {
    const currentlySelected = query.getEvent('selected');
    return {
      selected: currentlySelected ? currentlySelected.size() > 0 : false,
    };
  });

  const selectedNode = selected ? query.node(Array.from(selected)[0]) : null;
  const nodeData = selectedNode?.get();

  if (!selected || !nodeData) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Properties
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Settings className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
            <p className="text-neutral-500">
              Select a component to edit its properties
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handlePropChange = (prop: string, value: any) => {
    actions.setProp(Array.from(selected)[0], (props: any) => {
      props[prop] = value;
    });
  };

  const renderPropertyInputs = () => {
    if (!nodeData) return null;

    const props = nodeData.props || {};
    const inputs: React.ReactNode[] = [];

    // Common properties
    if (props.text !== undefined) {
      inputs.push(
        <div key="text" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Text
          </label>
          <Input
            value={props.text}
            onChange={(e) => handlePropChange('text', e.target.value)}
            placeholder="Enter text..."
          />
        </div>
      );
    }

    if (props.title !== undefined) {
      inputs.push(
        <div key="title" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Title
          </label>
          <Input
            value={props.title}
            onChange={(e) => handlePropChange('title', e.target.value)}
            placeholder="Enter title..."
          />
        </div>
      );
    }

    if (props.subtitle !== undefined) {
      inputs.push(
        <div key="subtitle" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Subtitle
          </label>
          <Input
            value={props.subtitle}
            onChange={(e) => handlePropChange('subtitle', e.target.value)}
            placeholder="Enter subtitle..."
          />
        </div>
      );
    }

    if (props.buttonText !== undefined) {
      inputs.push(
        <div key="buttonText" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Button Text
          </label>
          <Input
            value={props.buttonText}
            onChange={(e) => handlePropChange('buttonText', e.target.value)}
            placeholder="Enter button text..."
          />
        </div>
      );
    }

    if (props.buttonLink !== undefined) {
      inputs.push(
        <div key="buttonLink" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Button Link
          </label>
          <Input
            value={props.buttonLink}
            onChange={(e) => handlePropChange('buttonLink', e.target.value)}
            placeholder="Enter link URL..."
          />
        </div>
      );
    }

    if (props.src !== undefined) {
      inputs.push(
        <div key="src" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Image URL
          </label>
          <Input
            value={props.src}
            onChange={(e) => handlePropChange('src', e.target.value)}
            placeholder="Enter image URL..."
          />
        </div>
      );
    }

    if (props.alt !== undefined) {
      inputs.push(
        <div key="alt" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Alt Text
          </label>
          <Input
            value={props.alt}
            onChange={(e) => handlePropChange('alt', e.target.value)}
            placeholder="Enter alt text..."
          />
        </div>
      );
    }

    // Color properties
    if (props.color !== undefined) {
      inputs.push(
        <div key="color" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Text Color
          </label>
          <div className="flex space-x-2">
            <Input
              value={props.color}
              onChange={(e) => handlePropChange('color', e.target.value)}
              placeholder="#000000"
              className="flex-1"
            />
            <input
              type="color"
              value={props.color}
              onChange={(e) => handlePropChange('color', e.target.value)}
              className="w-10 h-10 border border-neutral-300 rounded cursor-pointer"
            />
          </div>
        </div>
      );
    }

    if (props.backgroundColor !== undefined) {
      inputs.push(
        <div key="backgroundColor" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Background Color
          </label>
          <div className="flex space-x-2">
            <Input
              value={props.backgroundColor}
              onChange={(e) => handlePropChange('backgroundColor', e.target.value)}
              placeholder="#ffffff"
              className="flex-1"
            />
            <input
              type="color"
              value={props.backgroundColor}
              onChange={(e) => handlePropChange('backgroundColor', e.target.value)}
              className="w-10 h-10 border border-neutral-300 rounded cursor-pointer"
            />
          </div>
        </div>
      );
    }

    // Size properties
    if (props.fontSize !== undefined) {
      inputs.push(
        <div key="fontSize" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Font Size
          </label>
          <select
            value={props.fontSize}
            onChange={(e) => handlePropChange('fontSize', e.target.value)}
            className="input w-full"
          >
            <option value="sm">Small</option>
            <option value="base">Base</option>
            <option value="lg">Large</option>
            <option value="xl">Extra Large</option>
            <option value="2xl">2X Large</option>
            <option value="3xl">3X Large</option>
            <option value="4xl">4X Large</option>
          </select>
        </div>
      );
    }

    if (props.fontWeight !== undefined) {
      inputs.push(
        <div key="fontWeight" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Font Weight
          </label>
          <select
            value={props.fontWeight}
            onChange={(e) => handlePropChange('fontWeight', e.target.value)}
            className="input w-full"
          >
            <option value="normal">Normal</option>
            <option value="medium">Medium</option>
            <option value="semibold">Semi Bold</option>
            <option value="bold">Bold</option>
          </select>
        </div>
      );
    }

    if (props.textAlign !== undefined) {
      inputs.push(
        <div key="textAlign" className="space-y-2">
          <label className="block text-sm font-medium text-neutral-700">
            Text Align
          </label>
          <select
            value={props.textAlign}
            onChange={(e) => handlePropChange('textAlign', e.target.value)}
            className="input w-full"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
            <option value="justify">Justify</option>
          </select>
        </div>
      );
    }

    return inputs;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-neutral-200">
        <h3 className="text-lg font-semibold text-neutral-900">
          Properties
        </h3>
        <p className="text-sm text-neutral-500 mt-1">
          {nodeData.displayName || 'Component'}
        </p>
      </div>

      {/* Properties */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {renderPropertyInputs()}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-neutral-200 bg-neutral-50">
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (selected) {
                actions.delete(Array.from(selected)[0]);
              }
            }}
            className="flex-1"
          >
            Delete
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (selected) {
                actions.duplicate(Array.from(selected)[0]);
              }
            }}
            className="flex-1"
          >
            Duplicate
          </Button>
        </div>
      </div>
    </div>
  );
}
